import { cabinet } from '../../api/cabinet';
import type { CaseStage, CaseStateRef, FamilyStep, FamilyStepKey } from '../../api/types';
import { useI18n, useT } from '../../i18n';
import { formatDate } from '../../lib/format';
import { loc } from '../../lib/loc';
import { useAsync } from '../../lib/useAsync';
import { knownSteps, stepHintKey, stepLabelKey, stepMarks, stepsAsStages } from './stepModel';
import { StageProgress } from './StageProgress';

/** GET /cabinet/cases/{id}/steps; any error (404 while the backend is older, network) → null = use the workflow stages. */
export function useFamilySteps(caseId: string) {
  const { data, loading } = useAsync(async () => {
    try {
      return knownSteps((await cabinet.steps(caseId))?.items);
    } catch {
      return null;
    }
  }, [caseId]);
  return { steps: data && data.length ? data : null, loading };
}

/**
 * «Етапи» on the case page: the family steps (Прийняли справу → … → Поховання) with done/current/next markers and dates.
 * Only labels, dates and one calm hint for the current step: no places, no partner, no photos, no arrival times.
 * Old cases without steps keep showing the workflow stages.
 */
export function FamilySteps({ caseId, states }: { caseId: string; states: CaseStage[] }) {
  const t = useT();
  const locale = useI18n((s) => s.locale);
  const { steps, loading } = useFamilySteps(caseId);

  if (loading && !steps) return null;
  if (!steps) return <StageProgress states={states} />;

  const marks = stepMarks(steps);
  return (
    <ol className="mt-4" data-testid="family-steps">
      {steps.map((s, i) => {
        const m = marks[i] ?? 'next';
        const key = s.key as FamilyStepKey;
        const date = m !== 'next' && s.reached_at ? formatDate(s.reached_at, locale, false) : '';
        return (
          <li key={s.key} aria-current={m === 'current' ? 'step' : undefined} data-mark={m} className="row py-2">
            <div className={`flex min-h-[48px] items-center gap-4 text-[16px] ${m === 'next' ? 'text-lab' : ''} ${m === 'current' ? 'font-medium' : ''}`}>
              <i className={`mk ${m === 'done' ? 'mk-done' : m === 'current' ? 'mk-cur' : 'mk-next'}`} aria-hidden />
              <span className="min-w-0 flex-1">{t(stepLabelKey(key))}</span>
              <span className="text-right text-[13px] font-normal text-lab">
                {date ? <time dateTime={s.reached_at ?? undefined}>{date}</time> : t(m === 'done' ? 'case.stage.done' : m === 'current' ? 'case.stage.current' : 'case.stage.next')}
              </span>
            </div>
            {m === 'current' ? <p className="pb-2 pl-[26px] text-[14px] leading-relaxed text-txt">{t(stepHintKey(key, steps))}</p> : null}
          </li>
        );
      })}
    </ol>
  );
}

/** The step the family is at now (last reached; all reached → the last one); null while loading or without steps. */
export function nowStepKey(steps: FamilyStep[] | null): FamilyStepKey | null {
  if (!steps || !steps.length) return null;
  const marks = stepMarks(steps);
  const cur = marks.indexOf('current');
  if (cur >= 0 && (steps[cur]?.done || steps[cur]?.reached_at)) return steps[cur]!.key as FamilyStepKey;
  const last = [...steps].reverse().find((s) => s.done || !!s.reached_at);
  return last ? (last.key as FamilyStepKey) : null;
}

/**
 * «Зараз: …» — the same signal as the steps below: the family step when the case has steps (so a case whose workflow
 * stage lags behind never says «Нова заявка» next to a full progress bar), else the workflow stage.
 */
export function CaseNowLine({ steps, loading, state }: { steps: FamilyStep[] | null; loading: boolean; state: CaseStateRef | null | undefined }) {
  const t = useT();
  const locale = useI18n((s) => s.locale);
  const key = nowStepKey(steps);
  if (loading && !steps) return <p className="mt-3 min-h-[24px] text-[16px]" aria-hidden />;
  const text = key ? t(stepLabelKey(key)) : loc(state?.name, locale) || state?.key;
  if (!text) return null;
  return (
    <p className="mt-3 text-[16px]">
      <span className="text-lab">{t('cases.now')}: </span>
      <span className="font-medium">{text}</span>
    </p>
  );
}

/** Cases list card: «Зараз» line + compact progress line, from one steps request. */
export function CaseStatus({ caseId, state, states }: { caseId: string; state: CaseStateRef | null | undefined; states: CaseStage[] }) {
  const t = useT();
  const { steps, loading } = useFamilySteps(caseId);
  return (
    <>
      <CaseNowLine steps={steps} loading={loading} state={state} />
      <StageProgress states={steps ? stepsAsStages(steps, (k) => t(stepLabelKey(k))) : states} compact />
    </>
  );
}

/** Compact progress line on the cases list: family steps when the case has them, else the workflow stages. */
export function CaseProgressLine({ caseId, states }: { caseId: string; states: CaseStage[] }) {
  const t = useT();
  const { steps } = useFamilySteps(caseId);
  return <StageProgress states={steps ? stepsAsStages(steps, (k) => t(stepLabelKey(k))) : states} compact />;
}
